import { CHANNEL_SORTS, } from './channels.js';
/**
 * **Lọc tiền khỏi đáp án** cho người không có `revenue:view`.
 *
 * Rà soát bảo mật 31/10/2026, mục **S5**. Đợt 7 đã khép module `analytics`
 * (`boChiSoTien`, `locTheTien` trong `analytics.ts`), nhưng năm đường của
 * `dashboard` và `channels` thì để mở có chủ đích: ba trang đó lấy doanh thu
 * làm **trục chính**, nên bỏ trường đi là mất cột và mất cách sắp ⇒ cần một
 * nhánh giao diện. File này là phần máy chủ của nhánh đó.
 *
 * ## Luật
 *
 * 1. **Bỏ hẳn khoá**, không gửi `null`. Một khoá tên `grossRevenue` với giá trị
 *    `null` vẫn nói ra "ở đây có một con số tiền", và giao diện sẽ vẽ một ô `—`
 *    mà người xem không được phép biết là có. Với `dashboard.revenue` thì còn
 *    một lý do nữa: `null` đã mang nghĩa *"module 08 không trả được số"*, nên
 *    dùng lại `null` cho nghĩa *"bạn không được xem"* là trộn hai câu khác hẳn.
 * 2. **Thứ tự cũng là tiền.** `topThucNhan` và `revenueShare` bị bỏ **cả danh
 *    sách**, không chỉ các cột tiền: một danh sách năm kênh đã sắp theo thực
 *    nhận nói ra ai thu nhiều hơn ai, dù không in một con số nào.
 * 3. **Giữ phần cấu trúc.** `chiaSe` mất mọi con số nhưng còn nhóm, tên người
 *    và vai trò: hai cách xem "theo nhóm" / "theo người" của bảng Quản lý kênh
 *    đọc chúng, và đội ngũ kênh không phải một con số tiền.
 * 4. **`partnerName` và `kind` của `payment` thì giữ** — "kênh này đi Network
 *    nào" là một việc vận hành. Chỉ `splitLabel` ("Công ty 85% · Network 15%")
 *    bị bỏ, vì đó là tỷ lệ chia tiền.
 *
 * ## Vì sao nằm ở `shared`
 *
 * Giao diện cần **đúng** danh sách này để ẩn cột, ẩn ô và ẩn lựa chọn sắp xếp.
 * Hai bản sao của một luật là hai bản sẽ lệch nhau; `coXemTien` của `MeResponse`
 * quyết định ở web, `locTien*` quyết định ở máy chủ, và cả hai đọc cùng file.
 */
/* ------------------------------------------------------------------ */
/* Cách sắp xếp                                                        */
/* ------------------------------------------------------------------ */
/** Cách sắp **đọc một con số tiền** ⇒ chỉ dành cho người có `revenue:view`. */
export const CACH_SAP_CAN_TIEN = [
    'revenue',
    'rpm',
    'chia-nhan-su',
    'cong-ty-giu-lai',
    'phan-cua-toi',
];
/**
 * Cách sắp mặc định của người **không** xem được tiền.
 *
 * Mặc định của cả app là `revenue`; người không có quyền thì bảng của họ không
 * còn cột đó, nên mặc định phải đổi — không phải rơi về thứ tự tên, mà về
 * **lượt xem**: đó là con số họ làm việc với.
 */
export const CACH_SAP_KHONG_TIEN = 'views';
export function cachSapCanTien(sort) {
    return CACH_SAP_CAN_TIEN.includes(sort);
}
/** Các cách sắp mà người xem được phép chọn. */
export function cachSapXemDuoc(coXemTien) {
    return CHANNEL_SORTS.filter((s) => coXemTien || !cachSapCanTien(s));
}
/**
 * Cách sắp **an toàn** cho một người: mã cần tiền mà thiếu quyền thì đổi về
 * {@link CACH_SAP_KHONG_TIEN}.
 *
 * Không trả 400: `?sort=revenue` còn nằm trong link cũ, trong bookmark và trong
 * nút "quay lại" của trình duyệt. Đổi âm thầm sang một thứ tự hợp lệ là hành vi
 * đúng ở đây — người dùng thấy bảng, chỉ không thấy cột tiền.
 */
export function cachSapAnToan(sort, coXemTien) {
    return coXemTien || !cachSapCanTien(sort) ? sort : CACH_SAP_KHONG_TIEN;
}
/* ------------------------------------------------------------------ */
/* Module 01 — channels                                                */
/* ------------------------------------------------------------------ */
function locChiaSe(c) {
    const khongSo = (ds) => ds.map(({ userId, tenNguoi, avatarColor }) => ({ userId, tenNguoi, avatarColor }));
    return {
        teamId: c.teamId,
        tenNhom: c.tenNhom,
        truongNhom: khongSo(c.truongNhom),
        quanLy: khongSo(c.quanLy),
        editor: khongSo(c.editor),
        daChe: c.daChe,
        vaiTroCuaToi: c.vaiTroCuaToi,
        tamTinh: c.tamTinh,
        thieuNgayVanHanh: c.thieuNgayVanHanh,
        /* Bỏ: thucNhan, chiPhi, loiNhuan, chiaNhanSu, congTyGiu, phanCuaToi,
           pctCuaToi, tongPct, canhBaoVuotPct, kenhLo, chiaTheoThucNhan — và `pct`
           của từng pill. `kenhLo` cũng là tiền: nó nói lợi nhuận ≤ 0. */
    };
}
function locPayment(p) {
    const { splitLabel: _bo, ...conLai } = p;
    return conLai;
}
export function locTienDongKenh(d) {
    const { revenueSummary: _bo, payment, chiaSe, ...conLai } = d;
    return {
        ...conLai,
        payment: payment ? locPayment(payment) : payment,
        chiaSe: chiaSe ? locChiaSe(chiaSe) : chiaSe,
    };
}
export function locTienTongKenh(t) {
    const { grossRevenue: _g, networkShare: _n, companyNet: _c, avgRpm: _r, currency: _cu, ...conLai } = t;
    return conLai;
}
export function locTienDanhSachKenh(res) {
    return {
        ...res,
        items: res.items.map(locTienDongKenh),
        totals: locTienTongKenh(res.totals),
    };
}
export function locTienTomTatKenh(s) {
    const { netPercent: _np, grossDeltaPercent: _gd, topRpm: _tr, revenueShare: _rs, ...conLai } = s;
    return { ...conLai, totals: locTienTongKenh(s.totals) };
}
/* ------------------------------------------------------------------ */
/* Module 11 — dashboard                                               */
/* ------------------------------------------------------------------ */
export function locTienTongQuan(s) {
    const { revenue: _r, topChannels: _tc, ...conLai } = s;
    return conLai;
}
export function locTienTongQuanThem(e) {
    const { theoNguonTien: _nt, topThucNhan: _tt, chuoiTien: _ct, chiaNhanSu: _cn, ...conLai } = e;
    return conLai;
}
//# sourceMappingURL=locTien.js.map